import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ROLE_CODE_PATTERN,
  functionsApi,
  rolesApi,
  type FunctionModuleView,
  type RoleRequest,
  type RoleView,
} from "../../api/access";
import type { ApiError } from "../../api/client";
import { formatApiError } from "../../lib/apiError";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { FunctionMatrix } from "../../components/FunctionMatrix";
import { closure, requiresMap } from "../../lib/effectiveAccess";

type BaseForm = { code: string; name: string; description: string; sortOrder: string; allFunctions: boolean };

const EMPTY_FORM: BaseForm = { code: "", name: "", description: "", sortOrder: "0", allFunctions: false };

/**
 * `/admin/uloge/nova` and `/admin/uloge/:id` — its own screen, not a modal
 * (sprint 7 §5): role data on the left, the function matrix on the right.
 * "All functions, including future ones" dims and disables the matrix; on
 * the system role that switch is locked.
 */
export default function AdminRoleEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canWrite = isNew ? hasPermission("roles:create") : hasPermission("roles:edit");
  const readOnly = !canWrite;

  const [modules, setModules] = useState<FunctionModuleView[] | null>(null);
  const [role, setRole] = useState<RoleView | null>(null);
  const [form, setForm] = useState<BaseForm>(EMPTY_FORM);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const applyRole = useCallback((r: RoleView) => {
    setRole(r);
    setForm({ code: r.code, name: r.name, description: r.description ?? "", sortOrder: String(r.sortOrder), allFunctions: r.allFunctions });
    setSelected(new Set(r.functionKeys));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    functionsApi.catalogue()
      .then((m) => { if (!cancelled) setModules(m); })
      .catch((err) => { if (!cancelled) setLoadError(formatApiError(err as ApiError)); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!id) { setRole(null); setForm(EMPTY_FORM); setSelected(new Set()); return; }
    let cancelled = false;
    setLoadError(null);
    rolesApi.get(id)
      .then((r) => { if (!cancelled) applyRole(r); })
      .catch((err) => { if (!cancelled) setLoadError(formatApiError(err as ApiError)); });
    return () => { cancelled = true; };
  }, [id, applyRole]);

  function set<K extends keyof BaseForm>(key: K, value: BaseForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  }

  const onCodeChange = useCallback((raw: string) => {
    // Codes are upper-case identifiers; normalise as the admin types (same as position kinds).
    set("code", raw.toUpperCase().replace(/[\s-]+/g, "_"));
  }, []);

  const codeValid = !isNew || ROLE_CODE_PATTERN.test(form.code);
  const functionCount = useMemo(() => modules?.reduce((n, m) => n + m.functions.length, 0) ?? 0, [modules]);
  // What the ticked functions bring along (§1.1) — held by members although not ticked.
  const impliedCount = useMemo(
    () => (modules ? closure(selected, requiresMap(modules)).size - selected.size : 0),
    [modules, selected],
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!codeValid) {
      setError(t("roles.error.code", "Šifra: veliko slovo pa 1–29 velikih slova, cifara ili donjih crta (npr. SMENOVODJA)."));
      return;
    }
    const body: RoleRequest = {
      code: role ? role.code : form.code,
      name: form.name.trim(),
      description: form.description.trim() || null,
      allFunctions: form.allFunctions,
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      functionKeys: form.allFunctions ? [] : [...selected],
    };
    setSubmitting(true);
    try {
      if (isNew) {
        await rolesApi.create(body);
        toast.success(t("roles.toast.created", "Uloga kreirana."));
      } else if (id) {
        await rolesApi.update(id, body);
        toast.success(t("roles.toast.saved", "Uloga sačuvana."));
      }
      navigate("/admin/uloge");
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "CODE_TAKEN") setError(t("roles.error.codeTaken", "Šifra je već zauzeta."));
      else if (ae.code === "SYSTEM_ROLE") setError(t("roles.error.systemRoleAll", "Sistemska uloga mora zadržati sve funkcije."));
      else setError(formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  const title = isNew
    ? t("roles.editor.newTitle", "Nova uloga")
    : role?.name ?? t("roles.editor.title", "Uloga");
  const ready = isNew || role !== null;
  const matrixDisabled = readOnly || form.allFunctions;

  return (
    <div>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span><Link to="/admin/uloge">{t("roles.title", "Uloge")}</Link></span>
            <span>{isNew ? t("roles.editor.newCrumb", "Nova") : role?.code ?? "…"}</span>
          </nav>
          <h1>{title}</h1>
          {role && (
            <div className="sub">
              {t("roles.editor.members", "{{count}} korisnika", { count: role.memberCount })}
              {role.system && <> · {t("roles.system", "Sistemska")}</>}
            </div>
          )}
        </div>
      </div>

      {loadError && <div style={errorBox} role="alert">{loadError}</div>}
      {!ready && !loadError && <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>}

      {ready && (
        <form onSubmit={handleSubmit} className="split-layout">
          <aside className="card split-side">
            <div className="card-h"><h3>{t("roles.editor.baseSection", "Podaci o ulozi")}</h3></div>
            <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {isNew && (
                <label className="field">
                  <span>{t("roles.field.code", "Šifra")} <span className="req-mark">*</span></span>
                  <input
                    className="input"
                    value={form.code}
                    onChange={(e) => onCodeChange(e.target.value)}
                    required
                    maxLength={30}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="SMENOVODJA"
                    style={{ fontFamily: "var(--mono)", textTransform: "uppercase" }}
                    aria-invalid={form.code.length > 0 && !codeValid}
                    disabled={readOnly}
                  />
                  <span className="muted" style={{ fontSize: 12 }}>
                    {t("roles.field.codeHint", "Velika slova, cifre i donja crta; počinje slovom; 2–30 znakova. Ne menja se posle kreiranja.")}
                  </span>
                </label>
              )}
              {!isNew && (
                <div className="field">
                  <span>{t("roles.field.code", "Šifra")}</span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 13 }}>{form.code}</span>
                </div>
              )}
              <label className="field">
                <span>{t("roles.field.name", "Naziv")} <span className="req-mark">*</span></span>
                <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={100} disabled={readOnly} placeholder="Smenovođa" />
              </label>
              <label className="field">
                <span>{t("roles.field.description", "Opis")}</span>
                <textarea className="input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} disabled={readOnly} style={textarea} maxLength={500} />
              </label>
              <label className="field">
                <span>{t("roles.field.sortOrder", "Redosled")}</span>
                <input className="input" type="number" inputMode="numeric" value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} disabled={readOnly} style={{ maxWidth: 120 }} />
              </label>
              <label className="check-row" style={{ alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  checked={form.allFunctions}
                  onChange={(e) => set("allFunctions", e.target.checked)}
                  disabled={readOnly || role?.system === true}
                  style={{ marginTop: 3 }}
                  aria-label={t("roles.field.allFunctions", "Sve funkcije, uključujući buduće")}
                />
                <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span>{t("roles.field.allFunctions", "Sve funkcije, uključujući buduće")}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {role?.system
                      ? t("roles.field.allFunctionsLocked", "Sistemska uloga uvek ima sve funkcije.")
                      : t("roles.field.allFunctionsHint", "Svaka funkcija koja se kasnije doda u aplikaciju automatski pripada ovoj ulozi.")}
                  </span>
                </span>
              </label>
              <div className="muted" style={{ fontSize: 12.5 }}>
                {form.allFunctions
                  ? t("roles.editor.countAll", "Sve funkcije ({{total}})", { total: functionCount })
                  : impliedCount > 0
                    ? t("roles.editor.countImplied", "Izabrano {{count}} od {{total}} funkcija · uz njih dolazi još {{implied}}", { count: selected.size, total: functionCount, implied: impliedCount })
                    : t("roles.editor.count", "Izabrano {{count}} od {{total}} funkcija", { count: selected.size, total: functionCount })}
              </div>

              {error && <div style={errorBox} role="alert">{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn" onClick={() => navigate("/admin/uloge")}>
                  {readOnly ? t("action.back", "Nazad") : t("action.cancel", "Otkaži")}
                </button>
                {!readOnly && (
                  <button type="submit" className="btn primary" disabled={submitting || modules === null}>
                    {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
                  </button>
                )}
              </div>
            </div>
          </aside>

          <div style={{ minWidth: 0 }}>
            <div className="section-h">
              <h3>{t("roles.editor.functionsSection", "Funkcije")}</h3>
              <span className="sub">{t("roles.editor.functionsSub", "Šta članovi ove uloge smeju da rade.")}</span>
            </div>
            {modules === null ? (
              <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>
            ) : (
              <FunctionMatrix mode="role" modules={modules} selected={selected} onChange={(next) => { setSelected(next); setError(null); }} disabled={matrixDisabled} />
            )}
          </div>
        </form>
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
